/**
 * Performance monitoring and optimization utilities
 * Tracks Core Web Vitals and provides optimization hints
 */

// Track LCP (Largest Contentful Paint)
if (typeof window !== "undefined" && "PerformanceObserver" in window) {
  try {
    const lcpObserver = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      const lastEntry = entries[entries.length - 1] as any;

      // Log LCP for debugging (remove in production or send to analytics)
      if (process.env.NODE_ENV === "development") {
        console.log("🎯 LCP:", {
          value: lastEntry.renderTime || lastEntry.loadTime,
          element: lastEntry.element,
          url: lastEntry.url,
        });
      }
    });

    lcpObserver.observe({ entryTypes: ["largest-contentful-paint"] });
  } catch (e) {
    // PerformanceObserver not supported
  }

  // Track CLS (Cumulative Layout Shift)
  try {
    let clsValue = 0;
    const clsObserver = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        if (!(entry as any).hadRecentInput) {
          clsValue += (entry as any).value;
        }
      }

      if (process.env.NODE_ENV === "development") {
        console.log("📏 CLS:", clsValue);
      }
    });

    clsObserver.observe({ entryTypes: ["layout-shift"] });
  } catch (e) {
    // PerformanceObserver not supported
  }

  // Track FID (First Input Delay)
  try {
    const fidObserver = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      const firstInput = entries[0] as any;

      if (process.env.NODE_ENV === "development") {
        console.log("⚡ FID:", {
          delay: firstInput.processingStart - firstInput.startTime,
          name: firstInput.name,
        });
      }
    });

    fidObserver.observe({ entryTypes: ["first-input"] });
  } catch (e) {
    // PerformanceObserver not supported
  }
}

// Optimize resource hints
export function prefetchRoute(route: string) {
  if (typeof window !== "undefined" && "IntersectionObserver" in window) {
    const link = document.createElement("link");
    link.rel = "prefetch";
    link.href = route;
    document.head.appendChild(link);
  }
}

// Preconnect to API origin
export function preconnectAPI() {
  if (typeof window !== "undefined") {
    const link = document.createElement("link");
    link.rel = "preconnect";
    link.href = window.location.origin;
    document.head.appendChild(link);
  }
}

// Log navigation timing
if (typeof window !== "undefined" && window.performance) {
  window.addEventListener("load", () => {
    setTimeout(() => {
      const perfData = window.performance.timing;
      const pageLoadTime = perfData.loadEventEnd - perfData.navigationStart;
      const connectTime = perfData.responseEnd - perfData.requestStart;
      const renderTime = perfData.domComplete - perfData.domLoading;

      if (process.env.NODE_ENV === "development") {
        console.log("📊 Performance Metrics:", {
          pageLoad: `${pageLoadTime}ms`,
          connect: `${connectTime}ms`,
          render: `${renderTime}ms`,
        });
      }
    }, 0);
  });
}

export default {
  prefetchRoute,
  preconnectAPI,
};
